import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ArrowLeft, ShoppingBag, Heart, Share2, Users, Link as LinkIcon,
  Star, StarHalf, Minus, Plus, MessageSquare, Loader2,
  ChevronLeft, ChevronRight, Factory, Palette, Ruler, Tag as TagIcon,
  Check, X, MapPin, Clock, Award, Send, ShoppingCart, Zap,
  FileText, User, Mail, Phone, AlertCircle
} from 'lucide-react';
import { db } from '../../services/firebase';
import {
  doc, getDoc, updateDoc, arrayUnion, arrayRemove,
  serverTimestamp, setDoc, addDoc, collection, increment
} from 'firebase/firestore';
import { EnquiryModal } from './EnquiryModal';

// ============================================================================
// TYPES
// ============================================================================
interface DesignerItem {
  id: string;
  name: string;
  price: number;
  priceMin?: number;
  priceMax?: number;
  image: string;
  images?: string[];
  category: string;
  description: string;
  sellerId: string;
  sellerName: string;
  sellerAvatar?: string;
  sellerCompany?: string;
  sellerLocation?: string;
  rating: number;
  reviewCount: number;
  soldCount: number;
  reorderRate: number;
  isHot: boolean;
  isNew: boolean;
  discount?: number;
  moq: number;
  tags: string[];
  specs: Record<string, string>;
  customizationOptions: Array<{ name: string; minOrder: number; pricePerUnit: number }>;
  keyAttributes: Record<string, string>;
  likes?: string[];
  comments?: number;
  createdAt: any;
  colors?: string[];
  sizes?: string[];
  deals?: Array<{ quantity: string; price: string; label: string }>;
  media?: Array<{ type: 'image' | 'video'; url: string }>;
  workspaceId?: string;
  businessId?: string;
  uid?: string;
  photoURL?: string;
  logoUrl?: string;
  location?: string;
  businessName?: string;
  quickBuy?: boolean;
  isBulkItem?: boolean;
  requiresQuote?: boolean;
  stock?: number;
}

interface DesignerProductDetailModalProps {
  item: DesignerItem | null;
  isOpen: boolean;
  onClose: () => void;
  onToggleWishlist?: (id: string, type: 'product') => void;
  isWishlisted: boolean;
  userProfile?: any;
  currentUser?: any;
  onViewProfile?: (userId: string) => void;
  onSendMessage?: (sellerId: string, sellerName: string) => void;
  onAddToCart?: (item: any) => void;
  onBuyNow?: (item: any, quantity: number, color: string | null, size: string | null) => void;
}

interface Friend {
  uid: string;
  displayName?: string;
  username?: string;
  photoURL?: string;
}

interface Deal {
  quantity: string;
  price: string;
  label: string;
}

// ============================================================================
// 🟢 QUOTE CONFIRMATION MODAL
// ============================================================================
const QuoteConfirmationModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  item: DesignerItem | null;
  quantity: number;
  selectedColor: string | null;
  selectedSize: string | null;
  isSubmitting: boolean;
  buyerName: string;
  buyerEmail: string;
  buyerPhone: string;
  onBuyerInfoChange: (field: string, value: string) => void;
  errors: Record<string, string>;
}> = ({
  isOpen,
  onClose,
  onConfirm,
  item,
  quantity,
  selectedColor,
  selectedSize,
  isSubmitting,
  buyerName,
  buyerEmail,
  buyerPhone,
  onBuyerInfoChange,
  errors,
}) => {
  if (!isOpen || !item) return null;

  const totalPrice = item.price * quantity;

  return (
    <div className="fixed inset-0 z-[1300] flex items-center justify-center p-4 animate-in fade-in duration-200">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-3xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl animate-in zoom-in-95 duration-200">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-5 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <FileText size={20} className="text-orange-500" />
            <h3 className="text-lg font-black text-gray-900 uppercase tracking-tight">Confirm Quote</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* ⚠️ Your Details - Required */}
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
            <h4 className="text-[10px] font-black text-orange-600 uppercase tracking-widest flex items-center gap-2 mb-3">
              <User size={14} /> Your Details (Required)
            </h4>
            <div className="space-y-3">
              <div>
                <label className="text-[9px] font-bold text-gray-600 uppercase tracking-widest">Full Name *</label>
                <input
                  type="text"
                  placeholder="John Doe"
                  value={buyerName}
                  onChange={(e) => onBuyerInfoChange('name', e.target.value)}
                  className={`w-full bg-white border rounded-lg px-4 py-2.5 text-sm font-medium text-gray-800 outline-none transition-all ${
                    errors.name ? 'border-red-500 focus:ring-2 focus:ring-red-200' : 'border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-100'
                  }`}
                />
                {errors.name && <p className="text-[9px] text-red-500 font-medium">{errors.name}</p>}
              </div>
              <div>
                <label className="text-[9px] font-bold text-gray-600 uppercase tracking-widest">Email *</label>
                <input
                  type="email"
                  placeholder="john@example.com"
                  value={buyerEmail}
                  onChange={(e) => onBuyerInfoChange('email', e.target.value)}
                  className={`w-full bg-white border rounded-lg px-4 py-2.5 text-sm font-medium text-gray-800 outline-none transition-all ${
                    errors.email ? 'border-red-500 focus:ring-2 focus:ring-red-200' : 'border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-100'
                  }`}
                />
                {errors.email && <p className="text-[9px] text-red-500 font-medium">{errors.email}</p>}
              </div>
              <div>
                <label className="text-[9px] font-bold text-gray-600 uppercase tracking-widest">Phone Number *</label>
                <input
                  type="tel"
                  placeholder="+27 82 123 4567"
                  value={buyerPhone}
                  onChange={(e) => onBuyerInfoChange('phone', e.target.value)}
                  className={`w-full bg-white border rounded-lg px-4 py-2.5 text-sm font-medium text-gray-800 outline-none transition-all ${
                    errors.phone ? 'border-red-500 focus:ring-2 focus:ring-red-200' : 'border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-100'
                  }`}
                />
                {errors.phone && <p className="text-[9px] text-red-500 font-medium">{errors.phone}</p>}
              </div>
            </div>
          </div>

          {/* Product Summary */}
          <div className="border border-gray-200 rounded-xl p-4">
            <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Order Summary</h4>
            <div className="flex gap-3">
              <div className="w-14 h-14 rounded-lg bg-gray-100 overflow-hidden shrink-0">
                <img src={item.image || item.images?.[0]} className="w-full h-full object-cover" alt={item.name} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm text-gray-900 truncate">{item.name}</p>
                <p className="text-[10px] text-gray-500 uppercase tracking-widest">{item.category}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-gray-100">
              <div>
                <p className="text-[8px] font-bold text-gray-400 uppercase tracking-widest">Quantity</p>
                <p className="font-bold text-gray-900">{quantity} units</p>
              </div>
              <div>
                <p className="text-[8px] font-bold text-gray-400 uppercase tracking-widest">Price</p>
                <p className="font-bold text-orange-600">R{totalPrice.toFixed(2)}</p>
              </div>
              {selectedColor && (
                <div>
                  <p className="text-[8px] font-bold text-gray-400 uppercase tracking-widest">Color</p>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full border border-gray-200" style={{ backgroundColor: selectedColor }} />
                    <p className="font-bold text-gray-900 text-sm">{selectedColor}</p>
                  </div>
                </div>
              )}
              {selectedSize && (
                <div>
                  <p className="text-[8px] font-bold text-gray-400 uppercase tracking-widest">Size</p>
                  <p className="font-bold text-gray-900">{selectedSize}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-gray-200 p-5 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isSubmitting || !buyerName.trim() || !buyerEmail.trim() || !buyerPhone.trim()}
            className="flex-1 py-3 bg-orange-500 text-white rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-orange-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            {isSubmitting ? 'Sending...' : 'Send Quote'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// HELPER COMPONENTS
// ============================================================================
const ColorSwatch: React.FC<{ color: string; selected: boolean; onClick: () => void }> = ({ 
  color, selected, onClick 
}) => (
  <button
    onClick={onClick}
    className={`relative transition-all ${
      selected ? 'scale-110 ring-2 ring-orange-500 ring-offset-2' : 'hover:scale-105'
    }`}
  >
    <div 
      className="w-8 h-8 rounded-full border-2 border-gray-200 shadow-sm"
      style={{ backgroundColor: color }}
      title={color}
    />
    {selected && (
      <Check size={12} className="absolute -top-1 -right-1 text-orange-500 bg-white rounded-full" />
    )}
  </button>
);

const SizeBadge: React.FC<{ size: string; selected: boolean; onClick: () => void }> = ({ 
  size, selected, onClick 
}) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${
      selected
        ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/30'
        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
    }`}
  >
    {size}
  </button>
);

const DealBadge: React.FC<{ deal: Deal }> = ({ deal }) => (
  <div className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg">
    <TagIcon size={12} className="text-green-600" />
    <span className="text-[10px] font-black text-green-700">
      {deal.quantity} for R{deal.price}
    </span>
    {deal.label && (
      <span className="text-[8px] text-green-500 font-bold uppercase bg-green-100 px-1.5 py-0.5 rounded">
        {deal.label}
      </span>
    )}
  </div>
);

const StarRating: React.FC<{ rating: number }> = ({ rating }) => {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: fullStars }).map((_, i) => (
        <Star key={`full-${i}`} size={14} className="fill-orange-400 text-orange-400" />
      ))}
      {hasHalfStar && <StarHalf size={14} className="fill-orange-400 text-orange-400" />}
      {Array.from({ length: emptyStars }).map((_, i) => (
        <Star key={`empty-${i}`} size={14} className="text-gray-300" />
      ))}
    </div>
  );
};

// ============================================================================
// FRIEND SHARE OVERLAY
// ============================================================================
const FriendShareOverlay: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  friends: Friend[];
  onSend: (friendUid: string) => Promise<void>;
  itemName: string;
  loadingFriends: boolean;
}> = ({ isOpen, onClose, friends, onSend, itemName, loadingFriends }) => {
  const [search, setSearch] = useState('');
  const [sentIds, setSentIds] = useState<string[]>([]);

  const filteredFriends = useMemo(() => {
    const query = search.toLowerCase();
    return friends.filter(f => 
      (f.displayName || '').toLowerCase().includes(query) || 
      (f.username || '').toLowerCase().includes(query)
    );
  }, [friends, search]);

  const handleSend = async (friendUid: string) => {
    await onSend(friendUid);
    setSentIds(prev => [...prev, friendUid]);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1200] flex items-end lg:items-center justify-center p-0 lg:p-6 animate-in fade-in duration-200">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full lg:max-w-md bg-white rounded-t-[2.5rem] lg:rounded-3xl p-8 shadow-2xl animate-in slide-in-from-bottom-10 max-h-[90vh] flex flex-col">
        <div className="w-12 h-1.5 bg-gray-200 rounded-full mx-auto mb-6 lg:hidden" />
        <h3 className="text-black font-black uppercase italic tracking-widest text-xl mb-1">Send to Friends</h3>
        <p className="text-orange-500 text-[10px] font-bold uppercase tracking-widest mb-6">Sharing: {itemName}</p>
        
        <input 
          type="text" 
          placeholder="Search friends..." 
          className="w-full bg-gray-50 border border-gray-200 rounded-2xl py-3.5 px-5 text-black text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100 transition-all mb-4"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <div className="flex-1 overflow-y-auto space-y-2 scrollbar-hide">
          {loadingFriends ? (
            <div className="flex justify-center py-10">
              <Loader2 className="animate-spin text-orange-500" size={24} />
            </div>
          ) : filteredFriends.length > 0 ? (
            filteredFriends.map(friend => (
              <div key={friend.uid} className="flex items-center justify-between p-3 bg-white border border-gray-100 rounded-2xl hover:border-orange-200 transition-all">
                <div className="flex items-center gap-3">
                  <img 
                    src={friend.photoURL || `https://api.dicebear.com/7.x/avataaars/svg?seed=${friend.uid}`} 
                    className="w-10 h-10 rounded-full bg-orange-50" 
                    alt={friend.displayName || 'User'} 
                  />
                  <div>
                    <p className="text-black text-xs font-black uppercase">
                      {friend.displayName || 'User'}
                    </p>
                    <p className="text-gray-400 text-[10px] font-bold">
                      @{friend.username || 'user'}
                    </p>
                  </div>
                </div>
                <button 
                  onClick={() => handleSend(friend.uid)}
                  disabled={sentIds.includes(friend.uid)}
                  className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                    sentIds.includes(friend.uid) 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-orange-500 text-white hover:bg-orange-600'
                  }`}
                >
                  {sentIds.includes(friend.uid) ? 'Sent' : 'Send'}
                </button>
              </div>
            ))
          ) : (
            <p className="text-center py-8 text-gray-400 text-xs font-bold uppercase tracking-widest">
              No friends found.
            </p>
          )}
        </div>
        <button 
          onClick={onClose} 
          className="w-full mt-4 py-3 text-gray-500 hover:bg-gray-50 rounded-xl font-bold text-xs uppercase tracking-widest transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
export const DesignerProductDetailModal: React.FC<DesignerProductDetailModalProps> = ({
  item,
  isOpen,
  onClose,
  onToggleWishlist,
  isWishlisted,
  userProfile,
  currentUser,
  onViewProfile,
  onSendMessage,
  onAddToCart,
  onBuyNow,
}) => {
  // ============================================================================
  // STATE
  // ============================================================================
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showFriendOverlay, setShowFriendOverlay] = useState(false);
  const [userFriends, setUserFriends] = useState<Friend[]>([]);
  const [loadingFriends, setLoadingFriends] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAddToCartConfirm, setShowAddToCartConfirm] = useState(false);

  // 🟢 QUOTE CONFIRMATION STATE
  const [showQuoteConfirm, setShowQuoteConfirm] = useState(false);
  const [buyerInfo, setBuyerInfo] = useState({
    name: userProfile?.displayName || '',
    email: userProfile?.email || '',
    phone: userProfile?.phoneNumber || '',
  });
  const [buyerInfoErrors, setBuyerInfoErrors] = useState<Record<string, string>>({});

  // 🟢 ENQUIRY MODAL STATE
  const [showEnquiryModal, setShowEnquiryModal] = useState(false);
  const [selectedManufacturer, setSelectedManufacturer] = useState<any>(null);

  // 🟢 Determine if this item is a Bulk/Buy item (no quote needed)
  const isBulkItem = useMemo(() => {
    return item?.quickBuy === true || 
           item?.isBulkItem === true || 
           item?.requiresQuote === false ||
           (item?.category === 'Blanks' || item?.category === 'Blank Garments') ||
           (item?.category === 'Fabrics' || item?.category === 'Fabric Sourcing') ||
           (item?.category === 'Accessories') ||
           (item?.category === 'Caps' || item?.category === 'Hats') ||
           (item?.category === 'Bulk' || item?.category === 'Wholesale');
  }, [item]);

  // ============================================================================
  // DERIVED DATA
  // ============================================================================
  const sellerId = useMemo(() => {
    return item?.sellerId || item?.uid || item?.businessId || 
           item?.workspaceId || item?.seller_uid || item?.ownerId || '';
  }, [item]);

  const sellerDisplayName = useMemo(() => {
    return item?.sellerName || item?.sellerCompany || item?.businessName || 'Studio';
  }, [item]);

  const sellerLocation = useMemo(() => {
    return item?.sellerLocation || item?.location || 'Location not set';
  }, [item]);

  const images = useMemo(() => {
    if (!item) return [];
    
    const mediaImages = item.media?.filter(m => m.type === 'image').map(m => m.url) || [];
    const imageArray = item.images?.length ? item.images : [item.image];
    const allImages = mediaImages.length ? mediaImages : imageArray;
    
    return allImages.filter(Boolean).length ? allImages : ['https://placehold.co/600x600/eee/999?text=No+Image'];
  }, [item]);

  const bestDeal = useMemo(() => {
    return item?.deals?.length ? item.deals[0] : null;
  }, [item]);

  const hasMultipleDeals = useMemo(() => {
    return (item?.deals?.length || 0) > 1;
  }, [item]);

  // ============================================================================
  // EFFECTS
  // ============================================================================
  useEffect(() => {
    if (item) {
      setCurrentImageIndex(0);
      setQuantity(Math.max(1, item.moq || 1));
      setSelectedColor(null);
      setSelectedSize(null);
      setShowAddToCartConfirm(false);
      setShowQuoteConfirm(false);
      // Pre-fill buyer info from profile
      setBuyerInfo({
        name: userProfile?.displayName || '',
        email: userProfile?.email || '',
        phone: userProfile?.phoneNumber || '',
      });
      setBuyerInfoErrors({});
    }
  }, [item, userProfile]);

  useEffect(() => {
    if (!userProfile?.uid || !showFriendOverlay) return;

    const fetchFriends = async () => {
      setLoadingFriends(true);
      try {
        if (userProfile.followingUsers?.length > 0) {
          const friendDocs = await Promise.all(
            userProfile.followingUsers.map((uid: string) => getDoc(doc(db, 'users', uid)))
          );
          setUserFriends(
            friendDocs
              .filter(d => d.exists())
              .map(d => ({ uid: d.id, ...d.data() } as Friend))
          );
        }
      } catch (error) {
        console.error('Error fetching friends:', error);
      } finally {
        setLoadingFriends(false);
      }
    };

    fetchFriends();
  }, [userProfile, showFriendOverlay]);

  // ============================================================================
  // HANDLERS
  // ============================================================================
  const handleNextImage = useCallback(() => {
    setCurrentImageIndex(prev => (prev + 1) % images.length);
  }, [images.length]);

  const handlePrevImage = useCallback(() => {
    setCurrentImageIndex(prev => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  const handleViewProfile = useCallback(() => {
    if (sellerId && onViewProfile) {
      onClose();
      setTimeout(() => onViewProfile(sellerId), 300);
    } else {
      alert('This studio\'s profile could not be found.');
    }
  }, [sellerId, onViewProfile, onClose]);

  const handleCopyLink = useCallback(() => {
    const shareUrl = `${window.location.origin}/designer?product=${item?.id}`;
    navigator.clipboard.writeText(shareUrl);
    setShowShareMenu(false);
    alert('Link copied to clipboard!');
  }, [item?.id]);

  const handleNativeShare = useCallback(async () => {
    if (!item) return;
    
    const shareUrl = `${window.location.origin}/designer?product=${item.id}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: item.name,
          text: `Check out "${item.name}" on Designer Gate! 🔥`,
          url: shareUrl
        });
        setShowShareMenu(false);
      } catch (err) {
        console.error('Share failed:', err);
      }
    } else {
      handleCopyLink();
    }
  }, [item, handleCopyLink]);

  const handleSendToFriend = useCallback(async (friendUid: string) => {
    if (!currentUser?.uid || !item) return;

    try {
      const chatId = [currentUser.uid, friendUid].sort().join('_');
      
      await setDoc(doc(db, 'chats', chatId), {
        participants: [currentUser.uid, friendUid],
        updatedAt: serverTimestamp(),
        participantNames: {
          [currentUser.uid]: userProfile?.displayName || currentUser.displayName || 'User',
          [friendUid]: userFriends.find(f => f.uid === friendUid)?.displayName || 'User'
        },
        participantAvatars: {
          [currentUser.uid]: userProfile?.photoURL || '',
          [friendUid]: userFriends.find(f => f.uid === friendUid)?.photoURL || ''
        },
        lastMessage: `Check out "${item.name}" on Designer Gate!`,
        [`unreadCount.${friendUid}`]: increment(1)
      }, { merge: true });
      
      await addDoc(collection(db, 'chats', chatId, 'messages'), {
        text: `Check out "${item.name}" on Designer Gate!`,
        senderId: currentUser.uid,
        createdAt: serverTimestamp(),
        messageType: 'text',
        shareData: { 
          type: 'designer', 
          id: item.id, 
          name: item.name, 
          image: item.image, 
          price: item.price 
        }
      });
    } catch (error) {
      console.error('Failed to send:', error);
      alert('Failed to send message. Please try again.');
    }
  }, [currentUser, item, userProfile, userFriends]);

  // 🟢 OPEN QUOTE CONFIRMATION
  const openQuoteConfirmation = useCallback(() => {
    if (!currentUser) {
      alert('Please log in to request a quote.');
      return;
    }
    if (!item) return;

    // Validate required selections
    if (item.colors?.length && !selectedColor) {
      alert('Please select a color.');
      return;
    }
    if (item.sizes?.length && !selectedSize) {
      alert('Please select a size.');
      return;
    }

    // Pre-fill from profile
    setBuyerInfo({
      name: userProfile?.displayName || '',
      email: userProfile?.email || '',
      phone: userProfile?.phoneNumber || '',
    });
    setBuyerInfoErrors({});
    setShowQuoteConfirm(true);
  }, [currentUser, item, selectedColor, selectedSize, userProfile]);

  // 🟢 VALIDATE AND SEND QUOTE
  const validateAndSendQuote = useCallback(() => {
    const errors: Record<string, string> = {};
    
    if (!buyerInfo.name.trim()) errors.name = 'Full name is required';
    if (!buyerInfo.email.trim()) errors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(buyerInfo.email)) {
      errors.email = 'Please enter a valid email';
    }
    if (!buyerInfo.phone.trim()) errors.phone = 'Phone number is required';
    else if (buyerInfo.phone.replace(/\D/g, '').length < 9) {
      errors.phone = 'Please enter a valid phone number';
    }

    if (Object.keys(errors).length > 0) {
      setBuyerInfoErrors(errors);
      return;
    }

    setBuyerInfoErrors({});
    // Proceed with sending quote
    handleSendQuote();
  }, [buyerInfo]);

  // 🟢 SEND QUOTE
  const handleSendQuote = useCallback(async () => {
    if (!currentUser || !item) return;

    setIsSubmitting(true);
    try {
      await addDoc(collection(db, 'quotes'), {
        productId: item.id,
        productName: item.name,
        buyerId: currentUser.uid,
        buyerName: buyerInfo.name,
        buyerEmail: buyerInfo.email,
        buyerPhone: buyerInfo.phone,
        sellerId: sellerId,
        sellerName: sellerDisplayName,
        quantity: Math.max(quantity, item.moq || 1),
        selectedColor: selectedColor || 'N/A',
        selectedSize: selectedSize || 'N/A',
        status: 'pending',
        createdAt: serverTimestamp()
      });
      
      setShowQuoteConfirm(false);
      alert(`Quote request sent to ${sellerDisplayName}! They will contact you at ${buyerInfo.email} or ${buyerInfo.phone}.`);
      onClose();
    } catch (error) {
      console.error('Error sending quote request:', error);
      alert('Error sending request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [currentUser, item, buyerInfo, sellerId, sellerDisplayName, quantity, selectedColor, selectedSize, onClose]);

  // 🟢 ADD TO CART - Only for Bulk items
  const handleAddToCart = useCallback(() => {
    if (!currentUser) {
      alert('Please log in to add items to your cart.');
      return;
    }
    if (!item) return;

    // Validate required selections
    if (item.colors?.length && !selectedColor) {
      alert('Please select a color.');
      return;
    }
    if (item.sizes?.length && !selectedSize) {
      alert('Please select a size.');
      return;
    }

    const finalQuantity = Math.max(quantity, item.moq || 1);
    
    const cartItem = {
      id: item.id,
      name: item.name,
      price: item.price,
      quantity: finalQuantity,
      image: item.image || item.images?.[0] || '',
      images: item.images || [],
      sellerId: sellerId,
      sellerName: sellerDisplayName,
      businessId: sellerId,
      workspaceId: item.workspaceId || sellerId,
      selectedColor: selectedColor || 'Default',
      selectedSize: selectedSize || 'Standard',
      brand: sellerDisplayName,
      category: item.category || 'Product',
      moq: item.moq,
      leadTime: item.leadTime,
      stock: item.stock || 999,
      isBulkItem: true,
    };

    // Dispatch to cart
    if (onAddToCart) {
      onAddToCart(cartItem);
    } else {
      window.dispatchEvent(new CustomEvent('add-to-cart', {
        detail: { item: cartItem }
      }));
    }

    setShowAddToCartConfirm(true);
    setTimeout(() => setShowAddToCartConfirm(false), 2000);
  }, [currentUser, item, selectedColor, selectedSize, quantity, sellerId, sellerDisplayName, onAddToCart]);

  // 🟢 BUY NOW - Only for Bulk items
  const handleBuyNow = useCallback(() => {
    if (!currentUser) {
      alert('Please log in to continue.');
      return;
    }
    if (!item) return;

    if (item.colors?.length && !selectedColor) {
      alert('Please select a color.');
      return;
    }
    if (item.sizes?.length && !selectedSize) {
      alert('Please select a size.');
      return;
    }

    const finalQuantity = Math.max(quantity, item.moq || 1);
    const cartItem = {
      id: item.id,
      name: item.name,
      price: item.price,
      quantity: finalQuantity,
      image: item.image || item.images?.[0] || '',
      images: item.images || [],
      sellerId: sellerId,
      sellerName: sellerDisplayName,
      businessId: sellerId,
      workspaceId: item.workspaceId || sellerId,
      selectedColor: selectedColor || 'Default',
      selectedSize: selectedSize || 'Standard',
      brand: sellerDisplayName,
      category: item.category || 'Product',
      moq: item.moq,
      leadTime: item.leadTime,
      stock: item.stock || 999,
      isBulkItem: true,
    };

    // Open payment directly
    if (onBuyNow) {
      onBuyNow(cartItem, finalQuantity, selectedColor, selectedSize);
    } else {
      window.dispatchEvent(new CustomEvent('open-payment', {
        detail: {
          directPurchase: {
            item: cartItem,
            quantity: finalQuantity,
          }
        }
      }));
      onClose();
    }
  }, [currentUser, item, selectedColor, selectedSize, quantity, sellerId, sellerDisplayName, onBuyNow, onClose]);

  // 🟢 ENQUIRE HANDLER
  const handleEnquire = useCallback(() => {
    if (!item) return;
    
    if (!currentUser) {
      alert('Please log in to send an enquiry.');
      return;
    }

    setSelectedManufacturer({
      id: sellerId,
      businessName: sellerDisplayName,
      displayName: sellerDisplayName,
      photoURL: item.sellerAvatar || item.photoURL || item.logoUrl,
      logoUrl: item.logoUrl || item.sellerAvatar || item.photoURL,
      location: sellerLocation,
      moq: item.moq,
      leadTime: item.leadTime,
    });
    setShowEnquiryModal(true);
  }, [item, sellerId, sellerDisplayName, sellerLocation, currentUser]);

  // ============================================================================
  // EARLY RETURN
  // ============================================================================
  if (!isOpen || !item) return null;

  // ============================================================================
  // RENDER
  // ============================================================================
  return (
    <div className="fixed inset-0 z-[1100] bg-white flex flex-col lg:flex-row animate-in fade-in pb-safe">
      
      {/* Back Button */}
      <button 
        onClick={onClose} 
        className="absolute top-4 left-4 z-10 p-2.5 bg-black/50 backdrop-blur-sm text-white rounded-full hover:bg-black/70 transition-all shadow-lg"
      >
        <ArrowLeft size={20} />
      </button>

      {/* ================================================================ */}
      {/* LEFT: IMAGE CAROUSEL */}
      {/* ================================================================ */}
      <div className="w-full lg:w-[50%] h-[45vh] lg:h-full shrink-0 bg-gray-100 relative">
        <img 
          src={images[currentImageIndex]} 
          className="w-full h-full object-cover" 
          alt={item.name} 
        />
        
        {images.length > 1 && (
          <>
            <button 
              onClick={handlePrevImage} 
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 backdrop-blur-sm p-2 rounded-full shadow-lg text-white hover:bg-black/70 transition-colors"
            >
              <ChevronLeft size={20} />
            </button>
            <button 
              onClick={handleNextImage} 
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 backdrop-blur-sm p-2 rounded-full shadow-lg text-white hover:bg-black/70 transition-colors"
            >
              <ChevronRight size={20} />
            </button>
            <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-1.5">
              {images.map((_, idx) => (
                <div 
                  key={idx} 
                  className={`h-1 rounded-full transition-all ${
                    idx === currentImageIndex ? 'bg-white w-6' : 'bg-white/50 w-2'
                  }`} 
                />
              ))}
            </div>
          </>
        )}
        
        {item.isHot && (
          <span className="absolute top-4 right-4 bg-red-500 text-white text-[10px] font-black px-3 py-1.5 rounded-lg shadow-lg flex items-center gap-1">
            <span className="animate-pulse">●</span> HOT
          </span>
        )}
        
        {item.discount && (
          <span className="absolute bottom-4 right-4 bg-green-500 text-white text-[10px] font-black px-3 py-1.5 rounded-lg shadow-lg">
            -{item.discount}%
          </span>
        )}

        {/* 🟢 BULK ITEM BADGE */}
        {isBulkItem && (
          <span className="absolute bottom-4 left-4 bg-orange-500 text-white text-[10px] font-black px-3 py-1.5 rounded-lg shadow-lg flex items-center gap-1">
            <Zap size={12} /> Bulk Order
          </span>
        )}
      </div>

      {/* ================================================================ */}
      {/* RIGHT: DETAILS */}
      {/* ================================================================ */}
      <div className="w-full lg:w-[50%] flex flex-col bg-white overflow-hidden">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-gray-100 shrink-0">
          <div className="flex justify-between items-start">
            <div className="flex-1 min-w-0">
              <span className="text-[10px] font-black text-orange-500 uppercase tracking-widest bg-orange-50 px-2.5 py-0.5 rounded-md inline-block mb-1.5">
                {item.category || 'Designer'}
              </span>
              <h2 className="text-xl sm:text-2xl font-black text-gray-900 leading-tight truncate">
                {item.name}
              </h2>
              <div className="flex items-center gap-2 mt-1">
                <StarRating rating={item.rating || 4.5} />
                <span className="text-xs text-gray-500">
                  ({item.reviewCount || 0} reviews)
                </span>
              </div>
            </div>
            <div className="flex gap-1.5 ml-2 shrink-0">
              <button 
                onClick={() => onToggleWishlist?.(item.id, 'product')} 
                className="p-2 bg-white border border-gray-200 rounded-xl hover:border-orange-500 transition-colors"
              >
                <Heart 
                  size={18} 
                  fill={isWishlisted ? 'currentColor' : 'none'} 
                  className={isWishlisted ? 'text-red-500' : 'text-gray-600'} 
                />
              </button>
              <button 
                onClick={() => setShowShareMenu(true)} 
                className="p-2 bg-white border border-gray-200 rounded-xl hover:border-orange-500 transition-colors text-gray-600"
              >
                <Share2 size={18} />
              </button>
            </div>
          </div>
          
          {/* Pricing */}
          <div className="flex items-end gap-2 mt-2">
            <span className="text-2xl font-black text-orange-600">R{item.price}</span>
            {item.priceMax && item.priceMax > item.price && (
              <span className="text-sm text-gray-400 line-through mb-0.5">R{item.priceMax}</span>
            )}
          </div>
          
          {/* Deals & MOQ */}
          <div className="flex flex-wrap items-center gap-2 mt-1.5">
            {bestDeal && (
              <>
                <DealBadge deal={bestDeal} />
                {hasMultipleDeals && (
                  <span className="text-[10px] text-gray-400 font-bold">
                    +{item.deals!.length - 1} more
                  </span>
                )}
              </>
            )}
            <span className="text-[10px] font-bold text-gray-500 ml-auto">
              MOQ: <span className="text-gray-900">{item.moq} units</span>
            </span>
            {item.stock && (
              <span className="text-[10px] font-bold text-green-600">
                In Stock: {item.stock}
              </span>
            )}
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
          {/* Colors */}
          {item.colors && item.colors.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Palette size={14} className="text-gray-400" />
                <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Colors</h4>
              </div>
              <div className="flex flex-wrap gap-2">
                {item.colors.map(color => (
                  <ColorSwatch
                    key={color}
                    color={color}
                    selected={selectedColor === color}
                    onClick={() => setSelectedColor(selectedColor === color ? null : color)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Sizes */}
          {item.sizes && item.sizes.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Ruler size={14} className="text-gray-400" />
                <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Sizes</h4>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {item.sizes.map(size => (
                  <SizeBadge
                    key={size}
                    size={size}
                    selected={selectedSize === size}
                    onClick={() => setSelectedSize(selectedSize === size ? null : size)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Seller Info */}
          <div className="bg-gray-50 rounded-xl p-3 border border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white border border-gray-200 overflow-hidden flex shrink-0">
                <img 
                  src={item.sellerAvatar || item.photoURL || item.logoUrl || 'https://placehold.co/150x150/eee/999?text=Studio'} 
                  className="w-full h-full object-cover" 
                  alt={sellerDisplayName} 
                />
              </div>
              <div>
                <p className="font-bold text-sm text-gray-900">{sellerDisplayName}</p>
                <p className="text-[10px] text-gray-500 font-medium flex items-center gap-1">
                  <MapPin size={10} /> {sellerLocation}
                </p>
              </div>
            </div>
            <button 
              onClick={handleViewProfile}
              className="text-[10px] font-black uppercase text-orange-500 hover:text-orange-600 tracking-widest"
            >
              Visit Profile
            </button>
          </div>

          {/* Description */}
          <div>
            <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5">Description</h4>
            <p className="text-sm text-gray-700 leading-relaxed">{item.description || 'No description provided.'}</p>
          </div>

          {/* Specifications */}
          {item.specs && Object.keys(item.specs).length > 0 && (
            <div>
              <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5">Specifications</h4>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(item.specs).slice(0, 4).map(([key, value]) => (
                  <div key={key} className="bg-gray-50 border border-gray-100 p-2 rounded-lg">
                    <p className="text-[9px] text-gray-400 font-bold uppercase">{key}</p>
                    <p className="text-xs font-medium text-gray-900 truncate">{String(value)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Bulk Deals */}
          {item.deals && item.deals.length > 1 && (
            <div>
              <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5">Bulk Deals</h4>
              <div className="space-y-1.5">
                {item.deals.slice(1).map((deal, index) => (
                  <div key={index} className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2">
                      <TagIcon size={12} className="text-green-600" />
                      <span className="text-xs font-bold text-green-700">
                        {deal.quantity} for R{deal.price}
                      </span>
                    </div>
                    <span className="text-[10px] text-green-600 font-bold">
                      R{(Number(deal.price) / Number(deal.quantity)).toFixed(2)}/unit
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ================================================================ */}
        {/* 🟢 FOOTER ACTIONS */}
        {/* ================================================================ */}
        <div className="p-4 sm:p-6 bg-white border-t border-gray-100 space-y-2.5 shrink-0">
          
          {/* 🟢 QUANTITY SELECTOR - Always visible */}
          <div className="flex gap-3">
            <div className="flex items-center bg-gray-50 border border-gray-200 rounded-xl p-0.5 shrink-0">
              <button 
                onClick={() => setQuantity(Math.max(item.moq || 1, quantity - 1))} 
                className="w-9 h-9 flex items-center justify-center hover:bg-white rounded-lg text-gray-600"
              >
                <Minus size={14} />
              </button>
              <span className="w-8 text-center font-bold text-sm text-gray-900">
                {Math.max(quantity, item.moq || 1)}
              </span>
              <button 
                onClick={() => setQuantity(quantity + 1)} 
                className="w-9 h-9 flex items-center justify-center hover:bg-white rounded-lg text-gray-600"
              >
                <Plus size={14} />
              </button>
            </div>

            {/* 🟢 MAIN ACTION BUTTON - Changes based on item type */}
            {isBulkItem ? (
              // BULK ITEMS: Show "Add to Cart" + "Buy Now"
              <>
                <button 
                  onClick={handleAddToCart}
                  disabled={isSubmitting}
                  className={`flex-1 bg-gray-200 text-gray-700 rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-gray-300 transition-all flex items-center justify-center gap-2 ${
                    showAddToCartConfirm ? 'bg-green-500 text-white' : ''
                  }`}
                >
                  {showAddToCartConfirm ? (
                    <>✅ Added!</>
                  ) : (
                    <><ShoppingCart size={14} /> Add to Cart</>
                  )}
                </button>
                <button 
                  onClick={handleBuyNow}
                  className="flex-1 bg-orange-500 text-white rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-orange-600 shadow-lg shadow-orange-500/30 transition-all flex items-center justify-center gap-2"
                >
                  <Zap size={14} /> Buy Now
                </button>
              </>
            ) : (
              // NON-BULK ITEMS: Show "Request Quote"
              <button 
                onClick={openQuoteConfirmation}
                disabled={isSubmitting}
                className="flex-1 bg-orange-500 text-white rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-orange-600 shadow-lg shadow-orange-500/30 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isSubmitting ? <Loader2 className="animate-spin" size={14} /> : <FileText size={14} />}
                {isSubmitting ? 'Sending...' : 'Request Quote'}
              </button>
            )}
          </div>

          {/* 🟢 ENQUIRE BUTTON - Always visible for all items */}
          <button 
            onClick={handleEnquire}
            className="w-full py-2.5 bg-orange-500 text-white rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-orange-600 transition-colors flex items-center justify-center gap-2 border border-orange-500"
          >
            <Send size={14} /> 
            Enquire About This Product
          </button>
        </div>
      </div>

      {/* ================================================================ */}
      {/* SHARE MENU */}
      {/* ================================================================ */}
      {showShareMenu && (
        <div className="fixed inset-0 z-[1150] bg-black/50 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setShowShareMenu(false)}>
          <div className="bg-white rounded-3xl p-8 w-full max-w-sm text-center shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4 text-orange-500">
              <Share2 size={32} />
            </div>
            <h3 className="text-xl font-black uppercase tracking-tight mb-6 text-gray-900">Share Product</h3>
            <div className="space-y-3">
              <button 
                onClick={handleNativeShare} 
                className="w-full py-3.5 bg-orange-500 text-white rounded-xl font-bold text-sm flex items-center justify-center gap-2 hover:bg-orange-600"
              >
                <Users size={18} /> Share
              </button>
              <button 
                onClick={() => { setShowShareMenu(false); setShowFriendOverlay(true); }} 
                className="w-full py-3.5 bg-gray-50 text-gray-700 border border-gray-200 rounded-xl font-bold text-sm flex items-center justify-center gap-2 hover:bg-gray-100"
              >
                <Send size={18} /> Send to Friends
              </button>
              <button 
                onClick={handleCopyLink} 
                className="w-full py-3.5 bg-gray-50 text-gray-700 border border-gray-200 rounded-xl font-bold text-sm flex items-center justify-center gap-2 hover:bg-gray-100"
              >
                <LinkIcon size={18} /> Copy Link
              </button>
            </div>
            <button onClick={() => setShowShareMenu(false)} className="mt-4 text-xs font-bold text-gray-400 hover:text-gray-600 uppercase">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ================================================================ */}
      {/* FRIEND SHARE OVERLAY */}
      {/* ================================================================ */}
      <FriendShareOverlay 
        isOpen={showFriendOverlay}
        onClose={() => setShowFriendOverlay(false)}
        friends={userFriends}
        itemName={item.name}
        onSend={handleSendToFriend}
        loadingFriends={loadingFriends}
      />

      {/* ================================================================ */}
      {/* 🟢 QUOTE CONFIRMATION MODAL */}
      {/* ================================================================ */}
      <QuoteConfirmationModal
        isOpen={showQuoteConfirm}
        onClose={() => {
          setShowQuoteConfirm(false);
          setBuyerInfoErrors({});
        }}
        onConfirm={validateAndSendQuote}
        item={item}
        quantity={Math.max(quantity, item.moq || 1)}
        selectedColor={selectedColor}
        selectedSize={selectedSize}
        isSubmitting={isSubmitting}
        buyerName={buyerInfo.name}
        buyerEmail={buyerInfo.email}
        buyerPhone={buyerInfo.phone}
        onBuyerInfoChange={(field, value) => {
          setBuyerInfo(prev => ({ ...prev, [field]: value }));
          if (buyerInfoErrors[field]) {
            setBuyerInfoErrors(prev => ({ ...prev, [field]: '' }));
          }
        }}
        errors={buyerInfoErrors}
      />

      {/* ================================================================ */}
      {/* 🟢 ENQUIRY MODAL */}
      {/* ================================================================ */}
      <EnquiryModal
        isOpen={showEnquiryModal}
        onClose={() => {
          setShowEnquiryModal(false);
          setSelectedManufacturer(null);
        }}
        manufacturer={selectedManufacturer}
        onEnquirySent={() => {
          setShowEnquiryModal(false);
          setSelectedManufacturer(null);
        }}
      />
    </div>
  );
};

export default DesignerProductDetailModal;